import { Link, useSearchParams } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { api } from "@/api/client";
import { useAsyncData } from "@/hooks/useAsyncData";
import { errorCode } from "@/lib/errors";
import TicketCard from "@/components/TicketCard";
import { Button } from "@/components/ui/button";
import { CheckCircle, Ticket, ArrowRight, Loader2, Mail, AlertCircle } from "lucide-react";

export default function OrderConfirmation() {
  const [params] = useSearchParams();
  const orderId = params.get("orderId");
  const { data: order, loading, error } = useAsyncData(
    () => (orderId ? api.orders.get(orderId) : Promise.reject(new Error("Missing order id"))),
    [orderId],
  );

  if (loading) return (
    <div className="flex justify-center items-center min-h-[70vh]">
      <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
    </div>
  );

  if (error || !order) {
    const notFound = !orderId || errorCode(error) === "NOT_FOUND";
    return (
      <div className="min-h-screen bg-gray-50 py-16 px-4">
        <div className="max-w-lg mx-auto text-center">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <AlertCircle className="h-10 w-10 text-amber-500" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">{notFound ? "We couldn't find that order" : "Something went wrong"}</h1>
          <p className="text-gray-500 mt-2 text-base">
            {notFound ? "It may belong to a different account, or the link is incomplete." : "Please try again in a moment."}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link to={createPageUrl("MyTickets")} className="flex-1">
              <Button className="w-full bg-indigo-600 hover:bg-indigo-700 h-11">
                <Ticket className="h-4 w-4 mr-2" /> View My Tickets
              </Button>
            </Link>
            <Link to={createPageUrl("Events")} className="flex-1">
              <Button variant="outline" className="w-full h-11">
                Browse Events <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-16 px-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-10">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="h-10 w-10 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">You're all set!</h1>
          <p className="text-gray-500 mt-2 text-base">
            Your ticket is confirmed. Check your email for a copy with your QR code.
          </p>
          <div className="inline-flex items-center gap-2 mt-4 bg-blue-50 text-blue-700 rounded-full px-4 py-1.5 text-sm">
            <Mail className="h-4 w-4" />
            Confirmation sent to {order.user_email}
          </div>
        </div>

        <div className="mb-8">
          <TicketCard order={order} />
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Link to={createPageUrl("MyTickets")} className="flex-1">
            <Button className="w-full bg-indigo-600 hover:bg-indigo-700 h-11">
              <Ticket className="h-4 w-4 mr-2" /> View My Tickets
            </Button>
          </Link>
          <Link to={createPageUrl("Events")} className="flex-1">
            <Button variant="outline" className="w-full h-11">
              Browse More <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
